import { useEffect, useRef } from 'react';
import { create } from 'zustand';

import { api } from '../lib/api.js';
import { logEditor } from './console.js';

/**
 * Assets browser state shared with asset fields:
 *
 * - `ping`: reveal a file in the Assets panel (Unity's ping from an object
 *   field), switching the folder and flashing the tile;
 * - favorites (per user and project, like Unity's Favorites / Blender's
 *   bookmarks) in localStorage;
 * - labels (Unity asset labels / Blender asset tags) in the project's
 *   versioned `prism.assets.json`, so the team shares them;
 * - view options (grid / list, tile size, type filter).
 */

export const LABELS_FILE = 'prism.assets.json';
export const LABELS_FORMAT = 'prism.assets/1';

export type AssetsViewMode = 'grid' | 'list';

export type AssetsSort = 'name' | 'type' | 'modified' | 'size';

export interface AssetsView {
  mode: AssetsViewMode;
  /** Tile size in px (grid) / row icon size (list). */
  size: number;
  /** Type filter ids (empty = all types). */
  types: string[];
  /** Sort order (Blender's file browser "Sort By"); folders stay first. */
  sort: AssetsSort;
  sortDesc: boolean;
  /** Names under grid tiles (Blender asset shelf "Names"). */
  showNames: boolean;
}

/** A search kept in Favorites (Unity's Project window "Save search"). */
export interface SavedSearch {
  name: string;
  query: string;
  types: string[];
}

export const TILE_MIN = 48;
export const TILE_MAX = 160;
const VIEW_KEY = 'prism.assets.view';
const DEFAULT_VIEW: AssetsView = { mode: 'grid', size: 76, types: [], sort: 'name', sortDesc: false, showNames: true };

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? ({ ...fallback, ...(JSON.parse(raw) as object) } as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode / quota: the option just is not remembered.
  }
}

const favoritesKey = (projectId: number) => `prism.assets.favorites.${projectId}`;
const searchesKey = (projectId: number) => `prism.assets.searches.${projectId}`;

function readSearches(projectId: number): SavedSearch[] {
  try {
    const raw = JSON.parse(localStorage.getItem(searchesKey(projectId)) ?? '[]') as unknown;
    return Array.isArray(raw)
      ? raw
          .filter((s): s is SavedSearch => !!s && typeof s.name === 'string' && typeof s.query === 'string')
          .map((s) => ({ name: s.name, query: s.query, types: Array.isArray(s.types) ? s.types.filter((t) => typeof t === 'string') : [] }))
      : [];
  } catch {
    return [];
  }
}

function readFavorites(projectId: number): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(favoritesKey(projectId)) ?? '[]') as unknown;
    return Array.isArray(raw) ? raw.filter((p): p is string => typeof p === 'string') : [];
  } catch {
    return [];
  }
}

/** Parses `prism.assets.json` → labels by path (unknown shapes are ignored). */
export function parseLabels(text: string): Record<string, string[]> {
  try {
    const raw = JSON.parse(text) as { labels?: unknown };
    const out: Record<string, string[]> = {};
    if (raw.labels && typeof raw.labels === 'object' && !Array.isArray(raw.labels)) {
      for (const [path, list] of Object.entries(raw.labels as Record<string, unknown>)) {
        if (Array.isArray(list)) {
          const clean = [...new Set(list.filter((l): l is string => typeof l === 'string' && l.trim() !== '').map((l) => l.trim()))];
          if (clean.length) out[path] = clean;
        }
      }
    }
    return out;
  } catch {
    return {};
  }
}

export function serializeLabels(labels: Record<string, string[]>): string {
  const sorted = Object.fromEntries(
    Object.entries(labels)
      .filter(([, l]) => l.length > 0)
      .sort(([a], [b]) => a.localeCompare(b)),
  );
  return `${JSON.stringify({ format: LABELS_FORMAT, labels: sorted }, null, 2)}\n`;
}

interface AssetsState {
  projectId: number | null;
  ping: { path: string; nonce: number } | null;
  favorites: string[];
  savedSearches: SavedSearch[];
  labels: Record<string, string[]>;
  /** `modifiedAt` of the labels file last read (reload when it changes on disk). */
  labelsVersion: string | null;
  view: AssetsView;

  /** Select the project whose favorites / labels are shown. */
  setProject: (projectId: number | null) => void;
  pingAsset: (path: string) => void;
  toggleFavorite: (path: string) => void;
  /** Adds (or replaces, by name) a saved search. */
  saveSearch: (search: SavedSearch) => void;
  removeSearch: (name: string) => void;
  loadLabels: (version: string | null) => Promise<void>;
  setLabels: (path: string, labels: string[]) => Promise<void>;
  setView: (patch: Partial<AssetsView>) => void;
}

let nonce = 0;

export const useAssets = create<AssetsState>((set, get) => ({
  projectId: null,
  ping: null,
  favorites: [],
  savedSearches: [],
  labels: {},
  labelsVersion: null,
  view: readJson(VIEW_KEY, DEFAULT_VIEW),

  setProject: (projectId) => {
    if (get().projectId === projectId) return;
    set({
      projectId,
      favorites: projectId ? readFavorites(projectId) : [],
      savedSearches: projectId ? readSearches(projectId) : [],
      labels: {},
      labelsVersion: null,
      ping: null,
    });
  },

  pingAsset: (path) => set({ ping: { path, nonce: ++nonce } }),

  toggleFavorite: (path) => {
    const { projectId, favorites } = get();
    const next = favorites.includes(path) ? favorites.filter((p) => p !== path) : [...favorites, path];
    set({ favorites: next });
    if (projectId) writeJson(favoritesKey(projectId), next);
  },

  saveSearch: (search) => {
    const { projectId, savedSearches } = get();
    const next = [...savedSearches.filter((s) => s.name !== search.name), search];
    set({ savedSearches: next });
    if (projectId) writeJson(searchesKey(projectId), next);
  },

  removeSearch: (name) => {
    const { projectId, savedSearches } = get();
    const next = savedSearches.filter((s) => s.name !== name);
    set({ savedSearches: next });
    if (projectId) writeJson(searchesKey(projectId), next);
  },

  loadLabels: async (version) => {
    const { projectId } = get();
    if (!projectId) return;
    if (version === null) {
      set({ labels: {}, labelsVersion: null });
      return;
    }
    if (get().labelsVersion === version) return;
    try {
      const res = await api.get<{ file: { content: string } }>(`/projects/${projectId}/file?path=${LABELS_FILE}`);
      if (get().projectId === projectId) set({ labels: parseLabels(res.file.content), labelsVersion: version });
    } catch {
      set({ labelsVersion: version });
    }
  },

  setLabels: async (path, list) => {
    const { projectId } = get();
    if (!projectId) return;
    const labels = { ...get().labels };
    const clean = [...new Set(list.map((l) => l.trim()).filter(Boolean))];
    if (clean.length) labels[path] = clean;
    else delete labels[path];
    set({ labels });
    try {
      await api.put(`/projects/${projectId}/file`, { path: LABELS_FILE, content: serializeLabels(labels) });
    } catch (error) {
      logEditor('error', `Could not save asset labels: ${error instanceof Error ? error.message : String(error)}`);
    }
  },

  setView: (patch) => {
    const view = { ...get().view, ...patch };
    set({ view });
    writeJson(VIEW_KEY, view);
  },
}));

/** Every label used in the project, sorted. */
export function allLabels(labels: Record<string, string[]>): string[] {
  return [...new Set(Object.values(labels).flat())].sort((a, b) => a.localeCompare(b));
}

/** Runs `reveal` whenever a file is pinged (e.g. to switch a bottom panel to the Assets tab). */
export function useRevealAssets(reveal: () => void): void {
  const ref = useRef(reveal);
  ref.current = reveal;
  useEffect(
    () =>
      useAssets.subscribe((state, prev) => {
        if (state.ping && state.ping !== prev.ping) ref.current();
      }),
    [],
  );
}
